import React, { useState, useMemo } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, startOfWeek, endOfWeek, addMonths, subMonths } from 'date-fns';
import { Tables } from '@/integrations/supabase/types';
import { getCurrentSingaporeDate } from '@/lib/dateUtils';
import { ProjectWithDetails } from '@/hooks/useProjects';
import {
  calculateDailyAllocations,
  formatMinutesToDuration,
  getUtilizationPercentage,
  getUtilizationColor,
  MINUTES_PER_WORKING_DAY,
  HOURS_PER_WORKING_DAY,
  isWorkingDay,
} from '@/lib/taskTimeUtils';

type StaffTodo = Tables<'staff_todos'> & { category?: string; project_id?: string };
type Project = Tables<'projects'>;

interface TasksCalendarViewProps {
  todos: StaffTodo[];
  staff: any[];
  onEdit: (todo: StaffTodo) => void;
  onView: (todo: StaffTodo) => void;
  onDelete: (id: string) => void;
  onUpdateTodo: (todo: StaffTodo) => void;
  showStaffName?: boolean;
  projects?: ProjectWithDetails[];
  getPriorityColor: (priority: string) => string;
}

const TasksCalendarView: React.FC<TasksCalendarViewProps> = ({
  todos,
  staff,
  onEdit,
  onView,
  onDelete,
  onUpdateTodo,
  showStaffName = false,
  projects,
  getPriorityColor,
}) => {
  const [currentMonth, setCurrentMonth] = useState(getCurrentSingaporeDate());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);

    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentMonth]);

  // Calculate task distributions in real-time and group by date
  const { tasksByDate, utilizationByDate } = useMemo(() => {
    const grouped: Record<string, Array<{ task: StaffTodo; minutesForDay: number }>> = {};
    const utilization: Record<string, number> = {};

    todos.forEach(todo => {
      // If task has all allocation fields, distribute across working days
      if (todo.start_date && todo.deadline && todo.estimated_duration) {
        const estimatedMinutes = todo.estimated_duration; // already in minutes
        const allocations = calculateDailyAllocations(
          todo.start_date,
          todo.deadline,
          estimatedMinutes
        );

        // Add task to each allocated date
        allocations.forEach(allocation => {
          if (!grouped[allocation.date]) {
            grouped[allocation.date] = [];
          }
          grouped[allocation.date].push({
            task: todo,
            minutesForDay: allocation.allocatedMinutes,
          });

          // Track utilization
          utilization[allocation.date] = (utilization[allocation.date] || 0) + allocation.allocatedMinutes;
        });
      } else if (todo.deadline) {
        // Fallback: Show on deadline only if no start_date/estimated_duration
        const dateKey = format(new Date(todo.deadline), 'yyyy-MM-dd');
        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }
        grouped[dateKey].push({
          task: todo,
          minutesForDay: 0, // No allocation calculated
        });
      }
    });

    // Sort tasks within each date by priority
    Object.keys(grouped).forEach(date => {
      const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
      grouped[date].sort((a, b) => priorityOrder[a.task.priority] - priorityOrder[b.task.priority]);
    });

    return { tasksByDate: grouped, utilizationByDate: utilization };
  }, [todos]);

  // Get tasks for selected date
  const selectedDateTasks = useMemo(() => {
    if (!selectedDate) return [];
    const dateKey = format(selectedDate, 'yyyy-MM-dd');
    return tasksByDate[dateKey] || [];
  }, [selectedDate, tasksByDate]);

  const handlePrevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const handleToday = () => {
    setCurrentMonth(getCurrentSingaporeDate());
  };

  const getStaffName = (staffId: string) => {
    const staffMember = staff?.find(s => s.id === staffId);
    return staffMember?.name || 'Unknown';
  };

  const isToday = (date: Date) => {
    return isSameDay(date, getCurrentSingaporeDate());
  };

  // Get utilization color classes
  const getUtilizationColorClass = (percentage: number) => {
    if (percentage > 100) return 'bg-red-500';
    if (percentage >= 80) return 'bg-orange-500';
    if (percentage >= 50) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  // Get utilization text color
  const getUtilizationTextColor = (percentage: number) => {
    if (percentage > 100) return 'text-red-700';
    if (percentage >= 80) return 'text-orange-700';
    if (percentage >= 50) return 'text-yellow-700';
    return 'text-green-700';
  };

  return (
    <div className="space-y-4">
      {/* Calendar Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h2 className="text-xl sm:text-2xl font-bold">
              {format(currentMonth, 'MMMM yyyy')}
            </h2>
            <div className="flex items-center gap-1 sm:gap-2">
              <Button onClick={handleToday} variant="outline" size="sm">
                Today
              </Button>
              <Button onClick={handlePrevMonth} variant="outline" size="icon">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button onClick={handleNextMonth} variant="outline" size="icon">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Calendar Grid */}
      <Card>
        <CardContent className="p-2 sm:p-4">
          {/* Day headers */}
          <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center font-semibold text-[10px] sm:text-sm text-gray-600 py-1 sm:py-2">
                <span className="hidden sm:inline">{day}</span>
                <span className="sm:hidden">{day.charAt(0)}</span>
              </div>
            ))}
          </div>

          {/* Calendar days */}
          <div className="grid grid-cols-7 gap-1 sm:gap-2">
            {calendarDays.map(day => {
              const dateKey = format(day, 'yyyy-MM-dd');
              const dayTasks = tasksByDate[dateKey] || [];
              const isCurrentMonth = isSameMonth(day, currentMonth);
              const isTodayDate = isToday(day);
              const isSelected = selectedDate && isSameDay(day, selectedDate);
              const isWorkDay = isWorkingDay(day);

              // Calculate utilization for this day
              const utilizationMinutes = utilizationByDate[dateKey] || 0;
              const utilizationPercent = getUtilizationPercentage(utilizationMinutes);
              const hasUtilization = utilizationMinutes > 0 && isWorkDay;

              return (
                <div
                  key={dateKey}
                  className={`
                    min-h-[140px] border rounded-lg p-2 cursor-pointer transition-all flex flex-col
                    ${isCurrentMonth ? 'bg-white' : 'bg-gray-50 opacity-50'}
                    ${isTodayDate ? 'border-blue-500 border-2' : 'border-gray-200'}
                    ${isSelected ? 'ring-2 ring-blue-300' : ''}
                    ${!isWorkDay ? 'bg-gray-100' : ''}
                    hover:shadow-md
                  `}
                  onClick={() => setSelectedDate(day)}
                >
                  {/* Date number and utilization */}
                  <div className="flex items-center justify-between mb-1">
                    <div className={`
                      text-sm font-semibold
                      ${isTodayDate ? 'text-blue-600' : isCurrentMonth ? 'text-gray-900' : 'text-gray-400'}
                    `}>
                      {format(day, 'd')}
                    </div>
                    {!isWorkDay && isCurrentMonth && (
                      <span className="text-[10px] text-gray-400 font-medium">Weekend</span>
                    )}
                  </div>

                  {/* Utilization bar for working days */}
                  {isWorkDay && isCurrentMonth && (
                    <div className="mb-2">
                      <div className="flex items-center justify-between text-[10px] mb-1">
                        <span className={`font-medium ${getUtilizationTextColor(utilizationPercent)}`}>
                          {formatMinutesToDuration(utilizationMinutes)} / {HOURS_PER_WORKING_DAY}h
                        </span>
                        <span className={`${getUtilizationTextColor(utilizationPercent)}`}>
                          {utilizationPercent}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full transition-all ${getUtilizationColorClass(utilizationPercent)}`}
                          style={{ width: `${Math.min(utilizationPercent, 100)}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Tasks for this day */}
                  <div className="space-y-1 flex-1 overflow-hidden">
                    {dayTasks.slice(0, 3).map(({task, minutesForDay}) => (
                      <div
                        key={task.id}
                        className={`
                          text-xs p-1 rounded cursor-pointer flex items-center gap-1
                          ${task.status === 'completed' ? 'opacity-50 line-through' : ''}
                          ${getPriorityColor(task.priority)}
                        `}
                        onClick={(e) => {
                          e.stopPropagation();
                          onView(task);
                        }}
                        title={`${task.title}${minutesForDay > 0 ? ` (${formatMinutesToDuration(minutesForDay)})` : ''}`}
                      >
                        <span className="truncate flex-1">{task.title}</span>
                        {minutesForDay > 0 && (
                          <span className="text-[10px] font-semibold shrink-0">
                            {formatMinutesToDuration(minutesForDay)}
                          </span>
                        )}
                      </div>
                    ))}
                    {dayTasks.length > 3 && (
                      <div className="text-xs text-gray-500 font-medium">
                        +{dayTasks.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Selected Date Tasks Detail */}
      {selectedDate && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">
                  Tasks for {format(selectedDate, 'MMMM d, yyyy')}
                  {selectedDateTasks.length > 0 && (
                    <span className="ml-2 text-sm text-gray-500">
                      ({selectedDateTasks.length} {selectedDateTasks.length === 1 ? 'task' : 'tasks'})
                    </span>
                  )}
                </h3>
                {isWorkingDay(selectedDate) && (() => {
                  const dateKey = format(selectedDate, 'yyyy-MM-dd');
                  const utilizationMinutes = utilizationByDate[dateKey] || 0;
                  const utilizationPercent = getUtilizationPercentage(utilizationMinutes);
                  return (
                    <div className="flex items-center gap-2 mt-2">
                      <Clock className="h-4 w-4 text-gray-500" />
                      <span className={`text-sm font-medium ${getUtilizationTextColor(utilizationPercent)}`}>
                        {formatMinutesToDuration(utilizationMinutes)} / {HOURS_PER_WORKING_DAY}h allocated ({utilizationPercent}%)
                      </span>
                    </div>
                  );
                })()}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {selectedDateTasks.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CalendarIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>No tasks scheduled for this date</p>
              </div>
            ) : (
              <div className="space-y-3">
                {selectedDateTasks.map(({task, minutesForDay}) => (
                  <div
                    key={task.id}
                    className={`
                      p-4 border rounded-lg hover:bg-gray-50 cursor-pointer
                      ${task.status === 'completed' || task.status === 'cancelled' ? 'opacity-60' : ''}
                    `}
                    onClick={() => onView(task)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className={`font-medium ${task.status === 'completed' || task.status === 'cancelled' ? 'line-through' : ''}`}>
                            {task.title}
                          </h4>
                          {minutesForDay > 0 && (
                            <Badge variant="outline" className="text-xs font-semibold">
                              <Clock className="h-3 w-3 mr-1" />
                              {formatMinutesToDuration(minutesForDay)}
                            </Badge>
                          )}
                        </div>
                        {task.description && (
                          <div
                            className="text-sm text-gray-600 mt-1 prose prose-sm max-w-none [&>p]:my-1"
                            dangerouslySetInnerHTML={{ __html: task.description }}
                          />
                        )}
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          {showStaffName && (
                            <Badge variant="outline" className="text-xs">
                              {getStaffName(task.staff_id)}
                            </Badge>
                          )}
                          <Badge className={getPriorityColor(task.priority)}>
                            {task.priority}
                          </Badge>
                          {task.category && (
                            <Badge variant="secondary" className="text-xs">
                              {task.category}
                            </Badge>
                          )}
                          {task.task_type && (
                            <Badge variant="outline" className={`text-xs ${task.task_type === 'Routine' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-orange-50 text-orange-700 border-orange-200'}`}>
                              {task.task_type}
                            </Badge>
                          )}
                          {task.project_id && (
                            <Badge variant="outline" className="text-xs">
                              {projects?.find(p => p.id === task.project_id)?.name || 'Project'}
                            </Badge>
                          )}
                          <Badge
                            variant="outline"
                            className={`text-xs ${
                              task.status === 'completed' ? 'text-green-600' :
                              task.status === 'cancelled' ? 'text-red-600' :
                              task.status === 'in-progress' ? 'text-blue-600' :
                              task.status === 'review' ? 'text-purple-600' :
                              task.status === 'on-hold' ? 'text-orange-600' : ''
                            }`}
                          >
                            {task.status}
                          </Badge>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(task);
                        }}
                      >
                        Edit
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tasks without deadline */}
      {todos.some(todo => !todo.deadline) && (
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold text-gray-600">
              Tasks without deadline
            </h3>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {todos?.filter(todo => !todo.deadline).slice(0, 5).map(task => (
                <div
                  key={task.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={() => onView(task)}
                >
                  <div className="flex items-center gap-2">
                    <Badge className={getPriorityColor(task.priority)}>
                      {task.priority}
                    </Badge>
                    <span className={task.status === 'completed' || task.status === 'cancelled' ? 'line-through' : ''}>
                      {task.title}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(task);
                    }}
                  >
                    Edit
                  </Button>
                </div>
              ))}
              {(todos?.filter(todo => !todo.deadline).length ?? 0) > 5 && (
                <p className="text-sm text-gray-500 text-center">
                  +{(todos?.filter(todo => !todo.deadline).length ?? 0) - 5} more tasks without deadline
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TasksCalendarView;
